import React, {ComponentProps, ReactNode, useCallback, useEffect} from "react";
import {Button, Tooltip as TooltipComponent} from "antd";

type Props<Tooltip extends boolean = false> = {
  tooltip?: Tooltip;
  onClick: (() => void) | (() => Promise<void>);
  disabled: boolean;
  text?: string;
  icon?: string;
  size?: "small" | "middle" | "large";
  style?: React.CSSProperties;
  loading?: boolean;
  loadingOnProcess?: boolean;
  tooltipProps?: Tooltip extends true ? ComponentProps<typeof TooltipComponent> : undefined;
  content?: ReactNode;
};

const GroupButton = <Tooltip extends boolean = false>({
  onClick,
  disabled,
  text,
  icon,
  size = "small",
  style,
  loading,
  loadingOnProcess,
  tooltip,
  tooltipProps = undefined,
  content,
}: Props<Tooltip>) => {
  const [localLoading, setLocalLoading] = React.useState(loading);

  useEffect(() => {
    setLocalLoading(loading)
  }, [loading]);

  const wrapper = useCallback((children: ReactNode) => {
    if(tooltip && tooltipProps) {
      return (
        <TooltipComponent
          {...tooltipProps}
        >
          <div style={{ minWidth: '24px' }}>
            {children}
          </div>
        </TooltipComponent>
      );
    } else {
      return <>
        {children}
      </>;
    }
  }, [tooltip, tooltipProps]);

  const clickHandle = useCallback(async () => {
    if(loadingOnProcess) {
      setLocalLoading(true);
      try {
        await onClick();
      } finally {
        setLocalLoading(false);
      }
    } else {
      onClick();
    }
  }, [loadingOnProcess, onClick]);

  return wrapper(
    <Button
      type="primary"
      className="group-btn"
      onClick={clickHandle}
      disabled={disabled}
      size={size}
      style={{
        minWidth: tooltip ? '24px' : 'auto',
        ...style
      }}
      loading={localLoading}
    >
      {icon && (
        <img src={icon} alt="refreshPage" style={{ marginBottom: "4px" }} />
      )}
      {text}
      {content && (
        <>
          {content}
        </>
      )}
    </Button>
  )
};

export default GroupButton;
